import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { io } from 'socket.io-client';
import api from '../api';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useAuth } from './AuthContext';

const CallContext = createContext(null);

export const useCall = () => {
  const ctx = useContext(CallContext);
  if (!ctx) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return ctx;
};

// Standard DTMF Audio Frequencies
const DTMF_FREQS = {
  '1': [697, 1209], '2': [697, 1336], '3': [697, 1477],
  '4': [770, 1209], '5': [770, 1336], '6': [770, 1477],
  '7': [852, 1209], '8': [852, 1336], '9': [852, 1477],
  '*': [941, 1209], '0': [941, 1336], '#': [941, 1477],
};

const getAudioContextClass = () => {
  if (typeof window === 'undefined') return null;
  return window.AudioContext || window.webkitAudioContext || null;
};

export const CallProvider = ({ children }) => {
  const { token } = useAuth();
  const [activeCall, setActiveCall] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeaker, setIsSpeaker] = useState(true);
  const [isKeypadOpen, setIsKeypadOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [dtmfDigits, setDtmfDigits] = useState('');

  const isMounted = useRef(true);
  const activeCallRef = useRef(null);
  const audioStreamRef = useRef(null);
  const ringAudioContextRef = useRef(null);
  const dtmfAudioContextRef = useRef(null);
  const ringIntervalRef = useRef(null);
  const timerRef = useRef(null);
  const endCallTimeoutRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);

  // Keep activeCallRef in sync without triggering callback recreation
  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  // Track component mounted lifecycle
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Stop telephone ring chime
  const stopRingingChime = useCallback(() => {
    if (ringIntervalRef.current) {
      clearInterval(ringIntervalRef.current);
      ringIntervalRef.current = null;
    }
    if (ringAudioContextRef.current && ringAudioContextRef.current.state !== 'closed') {
      ringAudioContextRef.current.close().catch(() => {});
      ringAudioContextRef.current = null;
    }
  }, []);

  // Play telephone ring chime
  const startRingingChime = useCallback(() => {
    try {
      const AudioContextClass = getAudioContextClass();
      if (!AudioContextClass) return;

      if (!ringAudioContextRef.current || ringAudioContextRef.current.state === 'closed') {
        ringAudioContextRef.current = new AudioContextClass();
      }
      if (ringAudioContextRef.current.state === 'suspended') {
        ringAudioContextRef.current.resume().catch(() => {});
      }

      const playTone = () => {
        if (!ringAudioContextRef.current || ringAudioContextRef.current.state === 'closed') return;
        try {
          const ctx = ringAudioContextRef.current;
          const osc1 = ctx.createOscillator();
          const osc2 = ctx.createOscillator();
          const gain = ctx.createGain();

          osc1.type = 'sine';
          osc2.type = 'sine';
          osc1.frequency.setValueAtTime(440, ctx.currentTime);
          osc2.frequency.setValueAtTime(480, ctx.currentTime);

          gain.gain.setValueAtTime(0.06, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.2);

          osc1.connect(gain);
          osc2.connect(gain);
          gain.connect(ctx.destination);

          osc1.start();
          osc2.start();
          osc1.stop(ctx.currentTime + 1.2);
          osc2.stop(ctx.currentTime + 1.2);
        } catch {}
      };

      playTone();
      if (ringIntervalRef.current) clearInterval(ringIntervalRef.current);
      ringIntervalRef.current = setInterval(playTone, 3200);
    } catch (e) {
      console.warn('[RING CHIME ERROR]', e.message);
    }
  }, []);

  // Play single DTMF tone on in-call dialpad tap
  const playDtmfTone = useCallback((key) => {
    const freqs = DTMF_FREQS[key];
    if (!freqs) return;

    try {
      const AudioContextClass = getAudioContextClass();
      if (!AudioContextClass) return;

      if (!dtmfAudioContextRef.current || dtmfAudioContextRef.current.state === 'closed') {
        dtmfAudioContextRef.current = new AudioContextClass();
      }
      const ctx = dtmfAudioContextRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }

      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gain = ctx.createGain();

      osc1.frequency.value = freqs[0];
      osc2.frequency.value = freqs[1];
      gain.gain.value = 0.08;

      osc1.connect(gain);
      osc2.connect(gain);
      gain.connect(ctx.destination);

      const now = ctx.currentTime;
      osc1.start(now);
      osc2.start(now);
      osc1.stop(now + 0.15);
      osc2.stop(now + 0.15);
    } catch {}
  }, []);

  // Acquire user microphone stream
  const acquireMicrophone = useCallback(async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        audioStreamRef.current = stream;
        return stream;
      }
    } catch (err) {
      console.warn('[MIC PERMISSION NOTE]', err.message);
    }
    return null;
  }, []);

  // Release user microphone stream
  const releaseMicrophone = useCallback(() => {
    if (audioStreamRef.current) {
      try {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      } catch {}
      audioStreamRef.current = null;
    }
  }, []);

  // Start real voice recording
  const startAudioRecording = useCallback((stream) => {
    if (!stream || typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') return;
    try {
      recordedChunksRef.current = [];
      let mimeType = 'audio/webm';
      if (typeof MediaRecorder.isTypeSupported === 'function') {
        if (!MediaRecorder.isTypeSupported('audio/webm')) {
          if (MediaRecorder.isTypeSupported('audio/mp4')) mimeType = 'audio/mp4';
          else if (MediaRecorder.isTypeSupported('audio/ogg')) mimeType = 'audio/ogg';
          else mimeType = '';
        }
      }

      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };
      recorder.start(1000);
      mediaRecorderRef.current = recorder;
    } catch (err) {
      console.warn('[CALL RECORDER INIT ERROR]', err.message);
    }
  }, []);

  // Reset in-call state
  const resetCallState = useCallback(() => {
    if (endCallTimeoutRef.current) {
      clearTimeout(endCallTimeoutRef.current);
      endCallTimeoutRef.current = null;
    }
    if (isMounted.current) {
      setActiveCall(null);
      setMinimized(false);
      setIsKeypadOpen(false);
      setDtmfDigits('');
      setIsMuted(false);
      setIsSpeaker(true);
    }
  }, []);

  // Start Outbound WhatsApp Voice Call
  const startCall = useCallback(async ({ phone, customerName }) => {
    const cleanPhone = String(phone || '').replace(/\D/g, '');
    if (cleanPhone.length < 5) {
      toast.error('Please enter a valid phone number');
      return;
    }

    if (endCallTimeoutRef.current) {
      clearTimeout(endCallTimeoutRef.current);
      endCallTimeoutRef.current = null;
    }

    // Stop any existing call state
    stopRingingChime();
    releaseMicrophone();

    const tempCall = {
      id: `temp_${Date.now()}`,
      callId: `call_${Date.now()}`,
      phone: cleanPhone,
      customerName: customerName || cleanPhone,
      direction: 'outbound',
      status: 'calling', // Stage 1: Initiating with Meta...
      duration: 0,
      createdAt: new Date().toISOString(),
    };

    setActiveCall(tempCall);
    setIsMuted(false);
    setIsSpeaker(true);
    setIsKeypadOpen(false);
    setMinimized(false);
    setDtmfDigits('');

    // Pre-acquire microphone
    acquireMicrophone();

    try {
      const { data } = await api.post('/calls/dial', { phone: cleanPhone });
      const serverCall = data.call || {};

      if (isMounted.current) {
        setActiveCall(prev => {
          if (!prev) return null;
          return {
            ...prev,
            id: serverCall._id || serverCall.id || prev.id,
            callId: serverCall.callId || prev.callId,
            status: 'ringing', // Ringing verified by Meta
          };
        });

        startRingingChime();
        toast.success(`Calling +${cleanPhone}...`);
      }
    } catch (err) {
      stopRingingChime();
      releaseMicrophone();
      if (isMounted.current) {
        setActiveCall(null);
      }

      const errData = err.response?.data || {};
      const errMsg = errData.message || errData.details || err.message || 'Call failed';
      const errTitle = errData.title || 'Meta WhatsApp Call Failed';

      Swal.fire({
        icon: 'error',
        title: errTitle,
        html: `
          <div style="text-align:left; font-size: 0.9rem; color: #334155; line-height: 1.5;">
            <p style="margin-bottom: 6px;"><strong>Meta Cloud API Response:</strong></p>
            <div style="background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 10px; color: #b91c1c; font-family: monospace; font-size: 0.82rem; word-break: break-word;">
              ${errMsg}
            </div>
            <p style="margin-top: 12px; font-size: 0.8rem; color: #64748b;">
              ⚠️ <strong>Notice:</strong> Outbound WhatsApp Voice Calling requires Calling API to be approved on your Meta WhatsApp Business Account (WABA) with messaging tier 2,000+.
            </p>
          </div>
        `,
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'Understood',
      });
    }
  }, [acquireMicrophone, releaseMicrophone, startRingingChime, stopRingingChime]);

  // Accept Incoming Call
  const acceptCall = useCallback(async () => {
    const currentCall = activeCallRef.current;
    if (!currentCall) return;

    stopRingingChime();
    await acquireMicrophone();

    const callId = currentCall.id || currentCall.callId;
    try {
      await api.patch(`/calls/${callId}/status`, { status: 'connected' });
    } catch {}

    if (isMounted.current) {
      setActiveCall(prev => prev ? { ...prev, status: 'connected', duration: 0 } : null);
      toast.success('WhatsApp Call Connected');
    }
  }, [acquireMicrophone, stopRingingChime]);

  // Decline Incoming Call
  const declineCall = useCallback(async () => {
    const currentCall = activeCallRef.current;
    if (!currentCall) return;

    stopRingingChime();
    releaseMicrophone();

    const callId = currentCall.id || currentCall.callId;
    try {
      await api.patch(`/calls/${callId}/status`, { status: 'rejected' });
    } catch {}

    if (isMounted.current) {
      setActiveCall(null);
      toast('Call Declined', { icon: '📵' });
    }
  }, [releaseMicrophone, stopRingingChime]);

  // End / Hang Up Call
  const endCall = useCallback(async () => {
    const currentCall = activeCallRef.current;
    if (!currentCall) return;

    stopRingingChime();

    const finalDuration = currentCall.duration || 0;
    const callId = currentCall.id || currentCall.callId;

    // 1. Stop MediaRecorder and grab recorded chunks
    let recordedBlob = null;
    let mime = 'audio/webm';
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mime = mediaRecorderRef.current.mimeType || 'audio/webm';
        mediaRecorderRef.current.stop();
      } catch {}
    }

    const recChunks = [...recordedChunksRef.current];
    recordedChunksRef.current = [];

    if (recChunks.length > 0 && finalDuration >= 1) {
      try {
        recordedBlob = new Blob(recChunks, { type: mime });
      } catch {}
    }

    releaseMicrophone();

    // Show 'ended' briefly for smooth transition
    if (isMounted.current) {
      setActiveCall(prev => prev ? { ...prev, status: 'ended' } : null);
    }

    try {
      await api.patch(`/calls/${callId}/status`, {
        status: finalDuration > 0 ? 'completed' : 'rejected',
        durationSeconds: finalDuration,
      });
    } catch {}

    // Upload voice recording if audio chunks exist
    if (recordedBlob && recordedBlob.size > 500) {
      const ext = mime.includes('mp4') ? 'mp4' : mime.includes('ogg') ? 'ogg' : 'webm';
      const formData = new FormData();
      formData.append('recording', recordedBlob, `call_${callId}.${ext}`);
      formData.append('duration', finalDuration);

      api.post(`/calls/${callId}/recording`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then(({ data }) => {
        toast.success('🎙️ Call Voice Recording Saved');
        window.dispatchEvent(new CustomEvent('whatsapp:call_completed', {
          detail: { callId, recordingUrl: data?.recordingUrl, duration: finalDuration }
        }));
      }).catch(err => {
        console.warn('[RECORDING UPLOAD ERROR]', err.message);
      });
    }

    // Dispatch global event so /calls table or any listener can auto-refresh
    window.dispatchEvent(new CustomEvent('whatsapp:call_completed', {
      detail: { callId, duration: finalDuration }
    }));

    if (endCallTimeoutRef.current) clearTimeout(endCallTimeoutRef.current);
    endCallTimeoutRef.current = setTimeout(resetCallState, 1200);
  }, [releaseMicrophone, stopRingingChime, resetCallState]);

  // Toggle Mute / Unmute
  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const next = !prev;
      if (audioStreamRef.current) {
        try {
          audioStreamRef.current.getAudioTracks().forEach(track => {
            track.enabled = !next;
          });
        } catch {}
      }
      toast(next ? 'Microphone Muted' : 'Microphone Active', {
        icon: next ? '🔇' : '🎙️',
        duration: 1500,
      });
      return next;
    });
  }, []);

  // Toggle Speaker
  const toggleSpeaker = useCallback(() => {
    setIsSpeaker(prev => {
      const next = !prev;
      toast(next ? 'Speaker On' : 'Earpiece Mode', {
        icon: next ? '🔊' : '🔈',
        duration: 1500,
      });
      return next;
    });
  }, []);

  // Toggle Keypad drawer
  const toggleKeypad = useCallback(() => {
    setIsKeypadOpen(prev => !prev);
  }, []);

  // Keypad button press
  const handleKeypadPress = useCallback((digit) => {
    playDtmfTone(digit);
    setDtmfDigits(prev => (prev.length < 24 ? prev + digit : prev));
  }, [playDtmfTone]);

  // Live Timer & Voice Recorder for Connected Calls
  useEffect(() => {
    if (activeCall?.status === 'connected') {
      // Start recording user conversation
      if (audioStreamRef.current) {
        startAudioRecording(audioStreamRef.current);
      } else {
        acquireMicrophone().then(stream => {
          if (stream) startAudioRecording(stream);
        });
      }

      timerRef.current = setInterval(() => {
        setActiveCall(prev => prev ? { ...prev, duration: (prev.duration || 0) + 1 } : null);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [activeCall?.status, startAudioRecording, acquireMicrophone]);

  // Socket IO Listener for Real-Time Incoming and Status Updates
  useEffect(() => {
    if (!token) return;
    const socketUrl = (process.env.REACT_APP_API_URL || 'https://api.xsquarehub.com/api').replace(/\/api\/?$/, '');
    const socket = io(socketUrl, { auth: { token }, transports: ['websocket', 'polling'] });

    socket.on('whatsapp:incoming_call', (callData) => {
      if (endCallTimeoutRef.current) {
        clearTimeout(endCallTimeoutRef.current);
        endCallTimeoutRef.current = null;
      }
      setActiveCall({
        ...callData,
        direction: 'inbound',
        status: 'ringing',
        duration: 0,
      });
      startRingingChime();
    });

    socket.on('whatsapp:call_status', (callData) => {
      setActiveCall(prev => {
        if (!prev) return null;
        if (prev.callId === callData.callId || prev.id === callData.id) {
          if (callData.status === 'completed' || callData.status === 'rejected') {
            stopRingingChime();
            releaseMicrophone();

            // Notify listeners that call finished
            window.dispatchEvent(new CustomEvent('whatsapp:call_completed', {
              detail: { callId: prev.callId || prev.id, duration: prev.duration || 0 }
            }));

            // Reset modal after short display transition
            if (endCallTimeoutRef.current) clearTimeout(endCallTimeoutRef.current);
            endCallTimeoutRef.current = setTimeout(resetCallState, 1200);

            return { ...prev, status: 'ended' };
          }
          if (callData.status === 'connected') {
            stopRingingChime();
            return { ...prev, status: 'connected' };
          }
          return { ...prev, status: callData.status };
        }
        return prev;
      });
    });

    return () => {
      socket.disconnect();
      stopRingingChime();
      releaseMicrophone();
      if (endCallTimeoutRef.current) {
        clearTimeout(endCallTimeoutRef.current);
      }
      if (dtmfAudioContextRef.current && dtmfAudioContextRef.current.state !== 'closed') {
        dtmfAudioContextRef.current.close().catch(() => {});
        dtmfAudioContextRef.current = null;
      }
    };
  }, [token, startRingingChime, stopRingingChime, releaseMicrophone, resetCallState]);

  const value = useMemo(() => ({
    activeCall,
    isMuted,
    isSpeaker,
    isKeypadOpen,
    minimized,
    dtmfDigits,
    startCall,
    acceptCall,
    declineCall,
    endCall,
    toggleMute,
    toggleSpeaker,
    toggleKeypad,
    setMinimized,
    handleKeypadPress,
    setDtmfDigits,
  }), [
    activeCall,
    isMuted,
    isSpeaker,
    isKeypadOpen,
    minimized,
    dtmfDigits,
    startCall,
    acceptCall,
    declineCall,
    endCall,
    toggleMute,
    toggleSpeaker,
    toggleKeypad,
    handleKeypadPress,
  ]);

  return (
    <CallContext.Provider value={value}>
      {children}
    </CallContext.Provider>
  );
};

export default CallContext;

